import { useEffect, useRef } from 'react';
import { loadDraft, saveDraft, type GameDraft } from '../lib/shop';
import { useClassroom } from '../state/Classroom';

/**
 * Keeps a game-in-progress in the database as well as the browser
 * (ocean_game_drafts), so the teacher can see what students are building and
 * the student finds it on another computer. Saves a couple of seconds after
 * the last change; an untouched new game isn't saved. If this browser had no
 * draft, the saved one is loaded.
 */
export function useServerDraft<T>(
  mode: GameDraft['mode'], title: string, level: T, blank: T, hadLocal: boolean,
  clean: (x: unknown) => T | null, apply: (title: string, level: T) => void,
) {
  const { active, session } = useClassroom();
  const cid = active?.id, sid = session?.user.id;
  const loaded = useRef(hadLocal);
  const blankJson = useRef(JSON.stringify(blank)).current;

  useEffect(() => {
    if (loaded.current || !cid || !sid) return;
    loaded.current = true;
    loadDraft(cid, sid, mode).then(d => { const l = d ? clean(d.content) : null; if (d && l) apply(d.title, l); }).catch(() => {});
  }, [cid, sid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!cid || !sid) return;
    if (!title.trim() && JSON.stringify(level) === blankJson) return;
    const id = window.setTimeout(() => { void saveDraft(cid, sid, mode, title, level).catch(() => {}); }, 2500);
    return () => window.clearTimeout(id);
  }, [title, level, cid, sid]); // eslint-disable-line react-hooks/exhaustive-deps
}
